import React, { useEffect, useState, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { indexMappings } from "../lib/ioMapping";
import { useSiteContext } from "../components/layout/Layout";
import { Activity, Cpu, Wifi, WifiOff, Zap, Thermometer, Droplets, Container, GitBranchPlus, ArrowDownToLine, Wind, Bell, Tag, Radio, AlertTriangle, TrendingUp, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import { primeAudio, playTing, playAlert } from "../lib/notificationSound";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { StatusCircle, TankGraphic } from "../components/acms/Indicators";
import { liveTank, tankLevelPct } from "../lib/tankLevel";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

// 4–20 mA loop endpoints for the pressure transmitters (tanks use lib/tankLevel.js, which
// supports voltage modes and configurable endpoints).
const SENSOR_MIN = 4;
const SENSOR_MAX = 20;

// Tank level, litres and signal status come from the one shared calculation (lib/tankLevel.js,
// mirror of backend/tank_level.py; /assets/live-values returns the backend's result as live.tank).

const TREND_ENABLED_TYPES = new Set(["pressure_sensor", "tank"]);

// /assets/live-values returns each register's last known value with its real last-seen time,
// even when the device behind it (e.g. one slave on a still-connected gateway) has gone quiet.
// A reading older than this is flagged on its card so an old value never passes as live.
const STALE_AFTER_MS = 5 * 60 * 1000;

function fmtAge(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "";
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m ago`;
  if (m < 1440) return `${Math.floor(m / 60)}h ago`;
  return `${Math.floor(m / 1440)}d ago`;
}

const TYPE_META = {
  pump:             { label: "Pump Status",         icon: Activity,        iconColor: "text-orange-400" },
  pressure_sensor:  { label: "Pressure Sensors",    icon: Activity,        iconColor: "text-sky-400" },
  tank:             { label: "Tanks",                icon: Container,       iconColor: "text-sky-400" },
  valve:            { label: "Valves",               icon: GitBranchPlus,   iconColor: "text-emerald-400" },
  lift:             { label: "Lifts",                icon: ArrowDownToLine, iconColor: "text-pink-400" },
  fan:              { label: "Fans & AHU",           icon: Wind,            iconColor: "text-cyan-400" },
  fire_alarm_device:{ label: "Fire Alarm Devices",   icon: Bell,            iconColor: "text-red-400" },
};
const TYPE_ORDER = ["pump", "pressure_sensor", "tank", "valve", "lift", "fire_alarm_device", "fan"];

const SIGNAL_LABEL = {
  DI: "Digital In",
  AI: "Analog In",
  DO: "Digital Out",
  digital_output: "Digital Out",
  digital_input: "Digital In",
  analog_input: "Analog In",
};

// ─── IO mapping footer strip — shown at the bottom of every wired asset card ─

function IOBadge({ mapping, asset }) {
  if (!mapping && !asset.io_point_name) return null;
  const sigLabel = mapping ? (SIGNAL_LABEL[mapping.signal_type] || mapping.signal_type) : null;
  const unit = mapping?.engineering_unit;
  const addr = mapping?.io_point_address;
  const desc = mapping?.description;
  const range = mapping && (mapping.engineering_min != null && mapping.engineering_max != null)
    ? `${mapping.engineering_min}–${mapping.engineering_max}${unit ? ` ${unit}` : ""}`
    : null;

  // Kept to a single line so cards stay short enough for the whole page to fit without
  // scrolling — range, gateway IMEI and description move into the hover tooltip.
  const tooltip = [
    desc,
    range && `Range: ${range}`,
    asset.gateway_id && `GW: ${asset.gateway_id}`,
  ].filter(Boolean).join("\n");

  return (
    <div className="mt-2 pt-1.5 border-t border-gray-100 flex items-center gap-2 min-w-0 overflow-hidden whitespace-nowrap" title={tooltip}>
      {/* Register name */}
      <span className="flex items-center gap-1 font-mono text-[10px] text-orange-500 font-semibold truncate">
        <Tag className="w-3 h-3 shrink-0" strokeWidth={2.5} />
        {asset.io_point_name}
      </span>
      {/* Signal type chip */}
      {sigLabel && (
        <span className="px-1 py-px rounded bg-sky-50 border border-sky-200 text-sky-600 text-[9px] font-semibold uppercase tracking-wide shrink-0">
          {sigLabel}
        </span>
      )}
      {/* Modbus address */}
      {addr && (
        <span className="flex items-center gap-1 text-[10px] text-gray-500 truncate">
          <Radio className="w-3 h-3 shrink-0" strokeWidth={2} />
          {addr}
        </span>
      )}
    </div>
  );
}

// ─── Card wrapper ────────────────────────────────────────────────────────────

function Card({ title, subtitle, ioMapping, asset, children, testid, trend }) {
  return (
    <div className="bg-white border border-gray-100 p-2 rounded-md flex flex-col min-w-0" data-testid={testid}>
      <div className="mb-2 min-w-0 pr-5">
        <h3 className="font-semibold text-xs uppercase tracking-wider text-gray-600 truncate" title={title}>{title}</h3>
        {subtitle && <p className="text-[10px] text-gray-500 truncate">{subtitle}</p>}
      </div>
      <div className="flex-1 flex flex-col justify-center">{children}</div>
      {trend}
      {asset && <IOBadge mapping={ioMapping} asset={asset} />}
    </div>
  );
}

// ─── Unlinked badge ───────────────────────────────────────────────────────────

function UnlinkedBadge({ asset, reason }) {
  return (
    <div className="flex flex-col items-center gap-2 py-4 text-center">
      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-slate-100 border border-slate-200 text-slate-600">
        {reason === "no_data" ? "Awaiting data" : "Not wired to live data"}
      </span>
      <span className="text-xs text-gray-500">Static status: <b className="text-gray-700">{asset.status || "active"}</b></span>
    </div>
  );
}

// ─── Stale card ───────────────────────────────────────────────────────────────
//
// Shown instead of the type's normal card once its latest reading is older than STALE_AFTER_MS.
// A fire-pump card that kept saying "Stopped" off a 3-day-old reading (DPS SANCTUM, 2026-10-12,
// RS-485 link down) read as a current state — the small "Stale" badge was easy to miss. The last
// state is still given, clearly as history. Analog cards (pressure, tank) don't repeat their last
// value: turning the raw 4–20 mA reading into bar / % needs each card's own config, and a wrong
// number here is worse than none — the 24h trend shows the real history.

const BINARY_LAST_LABEL = {
  pump: (v) => (v === 1 ? "Running" : "Stopped"),
  valve: (v) => (v === 1 ? "ON" : "OFF"),
  lift: (v) => (v === 1 ? "ON" : "OFF"),
  fan: (v) => (v === 1 ? "ON" : "OFF"),
  fire_alarm_device: (v) => (v === 1 ? "ON" : "OFF"),
};

function StaleCard({ type, asset, live, ioMapping, trend }) {
  const toLabel = BINARY_LAST_LABEL[type];
  const last = toLabel ? toLabel(Number(live.value)) : null;
  return (
    <Card title={asset.name} subtitle={asset.location_label} asset={asset} ioMapping={ioMapping} trend={trend} testid={`stale-card-${asset.asset_id}`}>
      <div className="flex flex-col items-center gap-1.5 py-3 text-center">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-slate-100 border border-slate-200 text-slate-600">
          <AlertTriangle className="w-3 h-3" strokeWidth={2.5} /> No data
        </span>
        <span className="text-[11px] text-gray-500" title={new Date(live.ingested_at).toLocaleString("en-IN")}>
          {last ? <>Last: <b className="text-gray-700">{last}</b> · {fmtAge(live.ingested_at)}</> : <>Last reading {fmtAge(live.ingested_at)}</>}
        </span>
        <span className="text-[10px] text-gray-400">Device not reporting — status unknown</span>
      </div>
    </Card>
  );
}

// ─── Pump card ────────────────────────────────────────────────────────────────

function PumpCard({ asset, live, ioMapping, trend }) {
  if (!live) return (
    <Card title={asset.name} subtitle={asset.location_label} asset={asset} ioMapping={ioMapping} trend={trend}>
      <UnlinkedBadge asset={asset} reason={asset.gateway_id ? "no_data" : "unlinked"} />
    </Card>
  );
  const running = Number(live.value) === 1;
  return (
    <Card title={asset.name} subtitle={asset.location_label} asset={asset} ioMapping={ioMapping} trend={trend}>
      <div className="flex justify-center py-1">
        <StatusCircle status={running ? "running" : "stopped"} subtitle={running ? "Running" : "Stopped"} />
      </div>
    </Card>
  );
}

// ─── 4–20 mA transmitters ─────────────────────────────────────────────────────
//
// Pressure = (mA-4)/16 × MaxPressureTransmitter, from the engineer's formula sheet (2026-08-11).
// Applies to every pressure sensor, whatever register it's on — this used to be special-cased to
// TATA Memorial's ch123/ch124, which drew any other site's ch124 sensor as TATA's 20 L test tank.

// Pressure transmitter config is NOT hardcoded — these are only the fallback used until the
// asset's own meta.range_bar / meta.alarm_low_bar / meta.alarm_high_bar are set via the
// Pressure Sensor registry form (registrySchemas.js already exposes them as required fields).
// DEFAULT_MAX_PRESSURE_BAR is the sensor's full-scale (0-40 bar transmitter) default — it must
// never be confused with DEFAULT_HIGH_PRESSURE_BAR (16), which is only an alarm setpoint. Percent
// / arc-fill math always divides by the full-scale max; the alarm limits are only used for the
// LOW/HIGH/NORMAL status label and color, never as the gauge's 100% denominator (a gauge that used
// the alarm-high value as its denominator would read ~2.5x fuller than reality and misfire into
// the LOW/red state early — pressure_gauge_scaling_bug.md).
const DEFAULT_MAX_PRESSURE_BAR = 40;
const DEFAULT_LOW_PRESSURE_BAR = 5;
const DEFAULT_HIGH_PRESSURE_BAR = 16;

// A 4–20 mA loop never legitimately reports below 4 mA in normal operation — a sample under
// that floor is a loop fault (open loop, 0 mA), not a real low reading. A single bad sample gets
// suppressed here (hold the last good value for one poll, confirm on the 2nd consecutive poll)
// so one-off ADC noise doesn't flash a false alarm.
//
// 2026-08-12: real production data (ch123, TATA Wing-B) showed this isn't always a one-off blip —
// the raw device payload itself alternated between a hard 0.0 and a valid ~8.5 on a sustained
// ~30s-long square wave, while ch123's sibling register on the exact same poll (ch124) stayed
// rock-steady the whole time. That pattern — one specific channel losing signal for whole polls
// at a time while its neighbor is fine — points to an intermittent physical connection (loose
// terminal, failing loop power) on that channel's wiring, not sensor noise. A longer debounce
// window would just hide a real fault for longer, which is the wrong tradeoff for a fire-safety
// reading. Instead we still show the confirmed value (never suppress a real, sustained fault) but
// also track how often the confirmed reading has crossed the SENSOR_MIN floor recently — repeated
// crossings mean "this channel's signal is unstable," a distinct condition from "pressure is
// genuinely low," and the UI should tell the difference so someone checks the wiring instead of
// reading it as a real pressure drop.
const UNSTABLE_WINDOW_MS = 3 * 60 * 1000;
const UNSTABLE_CROSSING_THRESHOLD = 2;

function useDebouncedAnalogRaw(live) {
  const goodRawRef = useRef(null);
  const pendingRef = useRef(0);
  const lastTsRef = useRef(null);
  const crossingsRef = useRef([]);
  const unstableRef = useRef(false);
  if (!live) return { raw: null, unstable: false };
  const raw = Number(live.value);
  if (live.ingested_at !== lastTsRef.current) {
    lastTsRef.current = live.ingested_at;
    const prevGood = goodRawRef.current;
    const accept = raw >= SENSOR_MIN || prevGood === null || pendingRef.current >= 1;
    if (accept) {
      const prevAboveFloor = prevGood === null ? null : prevGood >= SENSOR_MIN;
      const nowAboveFloor = raw >= SENSOR_MIN;
      goodRawRef.current = raw;
      pendingRef.current = 0;
      if (prevAboveFloor !== null && prevAboveFloor !== nowAboveFloor) {
        const nowMs = Date.parse(live.ingested_at) || Date.now();
        crossingsRef.current = [...crossingsRef.current, nowMs].filter((t) => t >= nowMs - UNSTABLE_WINDOW_MS);
      }
    } else {
      pendingRef.current += 1;
    }
    unstableRef.current = crossingsRef.current.length >= UNSTABLE_CROSSING_THRESHOLD;
  }
  return { raw: goodRawRef.current, unstable: unstableRef.current };
}

function LegacyArcGauge({ value, unit, pct, arcColor, rangeLabel }) {
  const circumference = Math.PI * 72;
  const arcStroke = pct !== null ? (pct / 100) * circumference : 0;
  return (
    <div className="relative max-w-[170px] mx-auto">
      <svg viewBox="0 0 200 120" className="w-full">
        <path d="M 20 110 A 80 80 0 0 1 180 110" fill="none" stroke="rgba(0,0,0,0.06)" strokeWidth="14" strokeLinecap="round" />
        <path d="M 20 110 A 80 80 0 0 1 180 110" fill="none" stroke={arcColor} strokeWidth="14" strokeLinecap="round"
          strokeDasharray={`${arcStroke} ${circumference}`} style={{ transition: "stroke-dasharray 0.6s, stroke 0.4s" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-end pb-2">
        <div className="flex items-baseline gap-1 leading-none">
          <span className="text-2xl font-extrabold tabular-nums" style={{ color: arcColor }}>{value.toFixed(2)}</span>
          <span className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{unit}</span>
        </div>
        <div className="text-[9px] text-gray-500 mt-1">{rangeLabel}</div>
      </div>
    </div>
  );
}

function PressureCard({ asset, live, ioMapping, trend }) {
  const { raw, unstable } = useDebouncedAnalogRaw(live);
  if (!live || raw === null) return (
    <Card title={asset.name} subtitle={asset.location_label} asset={asset} ioMapping={ioMapping} trend={trend}>
      <UnlinkedBadge asset={asset} reason={asset.gateway_id ? "no_data" : "unlinked"} />
    </Card>
  );
  // Full-scale must match whatever the gateway's IO Mapping (Hardware > Gateway config) says
  // the transmitter's real range is — that's the same "Range: X–Y bar" figure the IOBadge below
  // shows, and the number an on-site engineer actually calibrates against a physical gauge. Asset
  // Registry's own meta.range_bar is a separate, easily-stale field (nothing keeps it in sync
  // with the IO mapping) — it's only a fallback for assets that aren't wired to an IO mapping yet.
  const maxPressure = ioMapping?.engineering_max != null
    ? Number(ioMapping.engineering_max)
    : (Number(asset.meta?.range_bar) || DEFAULT_MAX_PRESSURE_BAR);
  const lowPressure = asset.meta?.alarm_low_bar != null ? Number(asset.meta.alarm_low_bar) : DEFAULT_LOW_PRESSURE_BAR;
  const highPressure = asset.meta?.alarm_high_bar != null ? Number(asset.meta.alarm_high_bar) : DEFAULT_HIGH_PRESSURE_BAR;
  // Pressure = (mA - 4) / 16 × Max Pressure Transmitter
  const value = Math.min(maxPressure, Math.max(0, ((raw - SENSOR_MIN) / (SENSOR_MAX - SENSOR_MIN)) * maxPressure));
  const pct = (value / maxPressure) * 100;
  const alarm = value < lowPressure ? "LOW" : value > highPressure ? "HIGH" : "NORMAL";
  const message = alarm === "NORMAL" ? "OK" : alarm === "LOW" ? "Low Pressure" : "High Pressure";
  const arcColor = unstable ? "#f59e0b" : alarm === "NORMAL" ? "#22c55e" : "#ef4444";
  return (
    <Card title={asset.name} subtitle={asset.location_label} asset={asset} ioMapping={ioMapping} trend={trend}>
      <LegacyArcGauge value={value} unit={live.unit || "bar"} pct={pct} arcColor={arcColor} rangeLabel={`of 0–${maxPressure} bar`} />
      <div className="flex flex-col items-center gap-1 mt-1">
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${
          unstable ? "bg-amber-50 border-amber-200 text-amber-700"
            : alarm === "NORMAL" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-red-50 border-red-200 text-red-700"
        }`}>
          {unstable ? <span className="inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" strokeWidth={2.5} />SIGNAL UNSTABLE</span> : alarm}
        </span>
        <span className="text-[11px] text-gray-500">{unstable ? `Reading is dropping in and out — check ${asset.io_point_name} wiring/loop` : message}</span>
      </div>
    </Card>
  );
}

// ─── Pressure sensor card ─────────────────────────────────────────────────────

// ─── Tank card ────────────────────────────────────────────────────────────────

// Status line under the tank: always text (colour is never the only indicator — client spec §9).
const TANK_STATUS_STYLE = {
  "UNDER-RANGE": "bg-amber-50 border-amber-200 text-amber-700",
  "OVER-RANGE": "bg-amber-50 border-amber-200 text-amber-700",
  "SENSOR FAULT": "bg-red-50 border-red-200 text-red-700",
  "CONFIG ERROR": "bg-slate-100 border-slate-200 text-slate-600",
  "NO SIGNAL": "bg-slate-100 border-slate-200 text-slate-600",
};

function TankCard({ asset, live, ioMapping, trend }) {
  if (!live) return (
    <Card title={asset.name} subtitle={asset.location_label} asset={asset} ioMapping={ioMapping} trend={trend}>
      <UnlinkedBadge asset={asset} reason={asset.gateway_id ? "no_data" : "unlinked"} />
    </Card>
  );
  const t = liveTank(asset, live);
  const signalText = t.signal == null ? "—" : `${Number(t.signal).toFixed(2)} ${t.unit || ""}`;
  // A faulty signal or broken configuration never draws a level — an empty-looking tank would read
  // as "no water" (client spec §5: a faulty signal must never become a valid empty/full reading).
  if (t.level_pct === null) {
    const fault = t.status === "SENSOR FAULT";
    return (
      <Card title={asset.name} subtitle={asset.location_label} asset={asset} ioMapping={ioMapping} trend={trend}>
        <div className="flex flex-col items-center gap-1.5 py-3 text-center" data-testid={`tank-${t.status.toLowerCase().replace(/\s+/g, "-")}`}>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${TANK_STATUS_STYLE[t.status] || TANK_STATUS_STYLE["CONFIG ERROR"]}`}>
            <AlertTriangle className="w-3 h-3" strokeWidth={2.5} /> {fault ? "Sensor fault" : t.status === "CONFIG ERROR" ? "Setup needed" : "No signal"}
          </span>
          <span className="text-[11px] text-gray-600">
            {fault ? "Level unknown — not an empty tank" : t.config_error || "No reading"}
          </span>
          <span className="text-[10px] text-gray-400">Signal {signalText}{fault ? ` (valid ${t.fault_low}–${t.fault_high} ${t.unit})` : ""}</span>
        </div>
      </Card>
    );
  }
  return (
    <Card title={asset.name} subtitle={asset.location_label} asset={asset} ioMapping={ioMapping} trend={trend}>
      <div className="flex justify-center py-1">
        <TankGraphic
          value={t.level_pct}
          label={asset.name}
          liters={t.litres}
          capacityLiters={t.capacity_l ? Math.round(t.capacity_l) : null}
        />
      </div>
      <div className="flex flex-col items-center gap-0.5 text-center">
        {t.status !== "OK" && (
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${TANK_STATUS_STYLE[t.status]}`}>
            {t.status === "UNDER-RANGE" ? "Under-range — shown as empty" : "Over-range — shown as full"}
          </span>
        )}
        <span className="text-[10px] text-gray-400">
          {t.depth_m != null ? `${t.depth_m.toFixed(2)} m · ` : ""}Signal {signalText}
        </span>
        {/* Hidden for now — range_note is still computed in lib/tankLevel.js */}
        {/* {t.range_note && <span className="text-[10px] text-amber-600" title={t.range_note}>⚠ Sensor range ≠ tank height</span>} */}
      </div>
    </Card>
  );
}

// ─── Trend card ───────────────────────────────────────────────────────────────

function TrendCard({ selectedSite, asset, unit, ioMapping }) {
  const [hist, setHist] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!selectedSite || !asset.io_point_name) return;
    let cancelled = false;
    // The API returns the raw register value (e.g. 4-20 mA for a pressure transmitter), not the
    // engineering value shown on the gauge above this chart — plotting it as-is silently mislabels
    // raw mA as bar, which is why the line sat flat near 0 instead of tracking the ~4-10 bar
    // readings. Apply the same mA -> bar conversion here, using the same full-scale source
    // (gateway IO Mapping's engineering_max, falling back to the asset's own meta.range_bar) as
    // the live gauge card, so the trend and the gauge always agree.
    const isPressure = asset.asset_type === "pressure_sensor";
    const isTank = asset.asset_type === "tank";
    const maxPressure = ioMapping?.engineering_max != null
      ? Number(ioMapping.engineering_max)
      : (Number(asset.meta?.range_bar) || DEFAULT_MAX_PRESSURE_BAR);
    const load = () => {
      api.get(`/v1/sites/${selectedSite}/telemetry/parameter-history`, {
        // Scope to the mapping's slave when it names one — the same register name can exist on
        // several slaves (AI-1 on both relay boards), and mixing them zig-zags the trend.
        params: {
          parameter: asset.io_point_name, hours: 24,
          ...(/_s\d+$/.test(ioMapping?.device_id || "") ? { device_id: ioMapping.device_id } : {}),
        },
      }).then((r) => {
        if (cancelled) return;
        setHist((r.data || []).map((d) => {
          const raw = Number(d.value);
          const value = isPressure
            ? Math.min(maxPressure, Math.max(0, ((raw - SENSOR_MIN) / (SENSOR_MAX - SENSOR_MIN)) * maxPressure))
            : isTank
              ? tankLevelPct(asset.meta, raw)
              : raw;
          return { ...d, value, t: new Date(d.ingested_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) };
        }));
        setLoaded(true);
      }).catch(() => { if (!cancelled) setLoaded(true); });
    };
    load();
    const i = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(i); };
  }, [selectedSite, asset.io_point_name, asset.asset_type, asset.meta?.range_bar, asset.meta?.tx_range_m, asset.meta?.height_m, ioMapping?.engineering_max]);

  return (
    <div className="w-full">
      {!loaded ? (
        <div className="h-[320px] flex items-center justify-center text-xs text-gray-400">Loading…</div>
      ) : hist.length === 0 ? (
        <div className="h-[320px] flex items-center justify-center text-xs text-gray-400">Accumulating history…</div>
      ) : (
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={hist} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="rgba(0,0,0,0.06)" strokeDasharray="3 3" />
            <XAxis dataKey="t" stroke="#94a3b8" fontSize={10} minTickGap={40} />
            <YAxis stroke="#94a3b8" fontSize={10} width={36} />
            <Tooltip formatter={(v) => [`${Number(v).toFixed(2)}${unit ? ` ${unit}` : ""}`, ""]} />
            <Line type="monotone" dataKey="value" stroke="#22c55e" strokeWidth={1.5} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ─── Generic status card for valve / lift / fan / fire_alarm_device ───────────

function StatusCard({ asset, live, ioMapping, trend }) {
  if (!live) {
    return (
      <Card title={asset.name} subtitle={asset.location_label} asset={asset} ioMapping={ioMapping} trend={trend}>
        <UnlinkedBadge asset={asset} reason={asset.gateway_id ? "no_data" : "unlinked"} />
      </Card>
    );
  }
  const on = Number(live.value) === 1;
  return (
    <Card title={asset.name} subtitle={asset.location_label} asset={asset} ioMapping={ioMapping} trend={trend}>
      <div className="flex justify-center py-1">
        <StatusCircle status={on ? "active" : "fault"} subtitle={on ? "ON" : "OFF"} />
      </div>
    </Card>
  );
}

const CARD_BY_TYPE = {
  pump: PumpCard,
  pressure_sensor: PressureCard,
  tank: TankCard,
  valve: StatusCard,
  lift: StatusCard,
  fan: StatusCard,
  fire_alarm_device: StatusCard,
};

// ─── Category block: one equipment category, cards scaled to fill the block ──
//
// Cards have a natural size (BASE per type). The block measures its own body with a
// ResizeObserver, tries every column count, and picks the one that gives the largest uniform
// scale; cards are then rendered with CSS zoom so everything — text, gauges, tank graphic —
// shrinks or grows together. Below lg (phones/tablets) blocks stack and cards keep natural size.
const CARD_BASE = {
  pump:              { w: 210, h: 220 },
  pressure_sensor:   { w: 220, h: 270 },
  tank:              { w: 220, h: 300 },
  default:           { w: 210, h: 200 },
};
const ZOOM_MIN = 0.7; // below this cards get cluttered — the block scrolls instead
const ZOOM_MAX = 1.4;
// Categories that may shrink to fit but are never enlarged beyond their natural size.
const NO_UPSCALE = new Set(["fire_alarm_device", "fan"]);
const BLOCK_GAP = 6;

function CategoryBlock({ type, assets, renderCard }) {
  const meta = TYPE_META[type];
  const Icon = meta.icon;
  const bodyRef = useRef(null);
  const [fit, setFit] = useState({ cols: 0, zoom: 1, natural: true });

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return undefined;
    const base = CARD_BASE[type] || CARD_BASE.default;
    const measure = () => {
      const W = el.clientWidth;
      const H = el.clientHeight;
      if (window.innerWidth < 1024 || !W || !H) { setFit({ cols: 0, zoom: 1, natural: true }); return; }
      const n = assets.length;
      let best = { cols: 1, zoom: 0 };
      for (let c = 1; c <= n; c += 1) {
        const r = Math.ceil(n / c);
        const cellW = (W - BLOCK_GAP * (c - 1)) / c;
        const cellH = (H - BLOCK_GAP * (r - 1)) / r;
        const z = Math.min(cellW / base.w, cellH / base.h);
        if (z > best.zoom) best = { cols: c, zoom: z };
      }
      const maxZoom = NO_UPSCALE.has(type) ? 1 : ZOOM_MAX;
      if (best.zoom >= 1 && maxZoom === 1) {
        // Room to spare: keep natural size and let cards flow side by side.
        setFit({ cols: 0, zoom: 1, natural: true });
        return;
      }
      setFit({ cols: best.cols, zoom: Math.max(ZOOM_MIN, Math.min(maxZoom, best.zoom)), natural: false });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => { ro.disconnect(); window.removeEventListener("resize", measure); };
  }, [type, assets.length]);

  return (
    <section className="glass rounded-md flex flex-col min-h-0 min-w-0 overflow-hidden snap-start" data-testid={`block-${type}`}>
      <div className="flex items-center gap-1.5 px-2 py-1 border-b border-gray-100 shrink-0">
        <Icon className={`w-4 h-4 ${meta.iconColor}`} strokeWidth={2.5} />
        <h2 className="text-xs uppercase tracking-wider font-bold text-gray-700">{meta.label}</h2>
        <span className="text-[10px] font-semibold text-gray-500 bg-gray-100 rounded-full px-1.5">{assets.length}</span>
      </div>
      <div ref={bodyRef} className={`flex-1 min-h-0 p-1 ${fit.natural ? "" : "overflow-auto"}`}>
        <div
          className="grid"
          style={fit.natural
            ? { gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: BLOCK_GAP }
            : { gridTemplateColumns: `repeat(${fit.cols}, minmax(0, 1fr))`, gap: BLOCK_GAP, alignContent: "start" }}
        >
          {assets.map((asset) => (
            <div key={asset.asset_id} className="min-w-0 min-h-0" style={fit.natural ? undefined : { zoom: fit.zoom }}>
              {renderCard(asset)}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Status-change sounds ─────────────────────────────────────────────────────
// The status Live Monitor rings on for pumps, fire alarm devices and tanks. null means no reading
// (unlinked, no data, gateway offline) — changes to or from null never ring, so a first load or a
// gateway coming back doesn't set it off. Tanks ring on their LOW / NORMAL / HIGH band, not on
// every level wobble, and only have bands once Level Low / High (%) are set on the asset.
const SOUND_TYPES = new Set(["pump", "fire_alarm_device", "tank"]);
const BAD_STATUSES = new Set(["alarm", "LOW", "HIGH", "SENSOR FAULT"]);
const SOUND_PREF_KEY = "liveMonitorSound";

function watchedStatus(asset, live) {
  if (!live || live.value == null) return null;
  const v = Number(live.value);
  if (asset.asset_type === "pump") return v === 1 ? "on" : "off";
  if (asset.asset_type === "fire_alarm_device") return v === 1 ? "alarm" : "normal";
  if (asset.asset_type === "tank") {
    // Same thresholds as the server-side tank alarms (tank_alarms.py / tank_level.py).
    const t = liveTank(asset, live);
    if (t.status === "SENSOR FAULT") return "SENSOR FAULT";
    if (t.level_pct == null) return null;
    if (t.alarms.low || t.alarms.very_low) return "LOW";
    if (t.alarms.high) return "HIGH";
    return "NORMAL";
  }
  return null;
}

function statusChangeText(asset, status, live) {
  if (asset.asset_type === "pump") return `${asset.name} ${status === "on" ? "started" : "stopped"}`;
  if (asset.asset_type === "fire_alarm_device") return status === "alarm" ? `${asset.name} — alarm triggered` : `${asset.name} back to normal`;
  if (status === "SENSOR FAULT") return `${asset.name} — level sensor fault (level unknown)`;
  const pct = liveTank(asset, live)?.level_pct;
  const lvl = pct == null ? "" : ` (${Math.round(pct)}%)`;
  return status === "NORMAL" ? `${asset.name} back to normal${lvl}` : `${asset.name} level ${status}${lvl}`;
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function LiveMonitor() {
  const { selectedSite, sites } = useSiteContext();
  // Desktop (lg+) gets the fixed-height block grid; smaller screens stack blocks in one column.
  const [isWide, setIsWide] = useState(() => typeof window !== "undefined" && window.innerWidth >= 1024);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const on = () => setIsWide(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  const [assets, setAssets] = useState([]);
  const [liveValues, setLiveValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [gatewayId, setGatewayId] = useState(null);
  const [allGateways, setAllGateways] = useState([]);
  const [ioMap, setIoMap] = useState({});
  const [gwStatusMap, setGwStatusMap] = useState({});
  const [sysvolt, setSysvolt] = useState(null);
  const [systemp, setSystemp] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [trendAsset, setTrendAsset] = useState(null);
  const [soundOn, setSoundOn] = useState(() => { try { return localStorage.getItem(SOUND_PREF_KEY) === "on"; } catch { return false; } });
  const soundOnRef = useRef(soundOn);
  const prevStatus = useRef({});
  const [flashIds, setFlashIds] = useState(() => new Set());
  const flashTimer = useRef(null);
  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    soundOnRef.current = next;
    try { localStorage.setItem(SOUND_PREF_KEY, next ? "on" : "off"); } catch { /* per-browser preference only */ }
    if (next) { primeAudio(); playTing(); }
  };

  const fetchAssets = useCallback(async () => {
    if (!selectedSite || selectedSite === "all") return;
    try {
      const r = await api.get("/assets", { params: { site_id: selectedSite } });
      setAssets(r.data || []);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, [selectedSite]);

  // Poll requests can overlap (a slow response from tick N can resolve after tick N+1's
  // fast response) — without sequencing, whichever call *resolves* last wins, not whichever
  // was *issued* last, so the UI can visibly flicker back to stale data. Each fetcher stamps
  // its own request with a ref counter and drops the response if a newer request has since
  // been issued.
  const liveValuesSeq = useRef(0);
  const gatewayHealthSeq = useRef(0);

  const fetchLiveValues = useCallback(async () => {
    if (!selectedSite || selectedSite === "all") return;
    const seq = ++liveValuesSeq.current;
    try {
      const r = await api.get("/assets/live-values", { params: { site_id: selectedSite } });
      if (seq !== liveValuesSeq.current) return; // a newer request already landed
      setLiveValues(r.data || {});
      const timestamps = Object.values(r.data || {}).map((v) => v.ingested_at).filter(Boolean);
      if (timestamps.length > 0)
        setLastUpdate(new Date(timestamps.reduce((a, b) => (a > b ? a : b))));
    } catch { /* ignore */ }
  }, [selectedSite]);

  const fetchGatewayHealth = useCallback(async () => {
    if (!selectedSite || selectedSite === "all") return;
    const seq = ++gatewayHealthSeq.current;
    try {
      const r = await api.get(`/v1/sites/${selectedSite}/telemetry/snapshot`);
      if (seq !== gatewayHealthSeq.current) return; // a newer request already landed
      const sv = r.data.find((d) => d.parameter === "sysvolt");
      const st = r.data.find((d) => d.parameter === "systemp");
      setSysvolt(sv ? sv.value : null);
      setSystemp(st ? st.value : null);
      const first = r.data.find((d) => d.gateway_id);
      if (first?.gateway_id) setGatewayId(first.gateway_id);
      // Fallback: use snapshot ingested_at if live-values had no linked assets
      const snapshotTs = r.data.map((d) => d.ingested_at).filter(Boolean).reduce((a, b) => (a > b ? a : b), null);
      if (snapshotTs) setLastUpdate((prev) => prev ?? new Date(snapshotTs));
    } catch { /* ignore */ }
  }, [selectedSite]);

  const fetchAllGateways = useCallback(async () => {
    if (!selectedSite || selectedSite === "all") return;
    try {
      const r = await api.get("/hardware/gateways");
      const gws = (r.data || []).filter((g) => g.site_id === selectedSite);
      setAllGateways(gws);

      // Fetch IO mappings for every gateway and build a flat lookup
      const mappingResults = await Promise.all(
        gws.map((gw) =>
          api.get(`/hardware/gateways/${gw.gateway_id}/io-mappings`)
            .then((res) => ({ gatewayId: gw.gateway_id, rows: res.data || [] }))
            .catch(() => ({ gatewayId: gw.gateway_id, rows: [] }))
        )
      );
      const map = {};
      for (const { gatewayId: gwId, rows } of mappingResults) {
        Object.assign(map, indexMappings(rows, gwId));
      }
      setIoMap(map);
    } catch { /* ignore */ }
  }, [selectedSite]);

  useEffect(() => {
    if (!selectedSite || selectedSite === "all") return;
    setLoading(true);
    setAssets([]);
    setLiveValues({});
    setAllGateways([]);
    setIoMap({});
    setGatewayId(null);
    fetchAssets();
    fetchLiveValues();
    fetchGatewayHealth();
    fetchAllGateways();
    const i = setInterval(() => { fetchLiveValues(); fetchGatewayHealth(); }, 5000);
    const j = setInterval(fetchAssets, 30000);
    const k = setInterval(fetchAllGateways, 30000);
    return () => { clearInterval(i); clearInterval(j); clearInterval(k); };
  }, [selectedSite, fetchAssets, fetchLiveValues, fetchGatewayHealth, fetchAllGateways]);

  const gwStatusSeq = useRef({});

  useEffect(() => {
    if (!allGateways.length) return;
    const loadAll = () => {
      allGateways.forEach((gw) => {
        const seq = (gwStatusSeq.current[gw.gateway_id] = (gwStatusSeq.current[gw.gateway_id] || 0) + 1);
        api.get(`/v1/gateways/${gw.gateway_id}/status`)
          .then((r) => {
            if (seq !== gwStatusSeq.current[gw.gateway_id]) return; // a newer request already landed
            setGwStatusMap((prev) => ({ ...prev, [gw.gateway_id]: r.data }));
          })
          .catch(() => {});
      });
    };
    loadAll();
    const i = setInterval(loadAll, 5000);
    return () => clearInterval(i);
  }, [allGateways]);

  const primaryGwId = gatewayId || allGateways[0]?.gateway_id;
  const gwStatus = gwStatusMap[primaryGwId] ?? null;

  const currentSite = sites.find((s) => s.site_id === selectedSite);

  // A different site starts fresh — its first readings are a baseline, not a change.
  useEffect(() => { prevStatus.current = {}; }, [selectedSite]);
  useEffect(() => {
    const changes = [];
    for (const asset of assets) {
      if (!SOUND_TYPES.has(asset.asset_type)) continue;
      const gwOffline = asset.gateway_id && gwStatusMap[asset.gateway_id]?.connected === false;
      const live = gwOffline ? undefined : liveValues[asset.asset_id];
      const status = watchedStatus(asset, live);
      const prev = prevStatus.current[asset.asset_id];
      prevStatus.current[asset.asset_id] = status;
      if (prev == null || status == null || prev === status) continue;
      changes.push({ asset, status, live });
    }
    if (!changes.length) return;
    const siteName = sites.find((s) => s.site_id === selectedSite)?.name;
    for (const c of changes) {
      (BAD_STATUSES.has(c.status) ? toast.error : toast.success)(statusChangeText(c.asset, c.status, c.live), { description: siteName });
    }
    if (soundOnRef.current) (changes.some((c) => BAD_STATUSES.has(c.status)) ? playAlert : playTing)();
    setFlashIds(new Set(changes.map((c) => c.asset.asset_id)));
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlashIds(new Set()), 1600);
  }, [liveValues, assets, gwStatusMap, sites, selectedSite]);
  useEffect(() => () => clearTimeout(flashTimer.current), []);

  if (!selectedSite || selectedSite === "all") {
    return <div className="text-gray-500 p-8">Select a site to view live telemetry.</div>;
  }

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div>
          <div className="h-3 bg-gray-100 rounded w-32 mb-2" />
          <div className="h-9 bg-gray-100 rounded w-72 mb-2" />
          <div className="h-2.5 bg-gray-50 rounded w-56" />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => <div key={i} className="glass rounded-lg p-5 h-40 bg-gray-50" />)}
        </div>
      </div>
    );
  }

  const byType = {};
  for (const a of assets) (byType[a.asset_type] ||= []).push(a);
  const hasAnyAssets = assets.length > 0;

  const fmtTs = (d) => d.toLocaleString("en-IN", { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });

  return (
    <div className="space-y-3" data-testid="acms-live-monitor">
      {/* Header — one compact row: site name, connection, gateway health, last data */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-baseline gap-3 min-w-0">
          <h1 className="text-xl md:text-2xl font-extrabold tracking-tight truncate">{currentSite?.name || selectedSite}</h1>
          <span className="text-[10px] uppercase tracking-wider text-orange-400 shrink-0">ACMS Live Monitor</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <button
            type="button"
            onClick={toggleSound}
            aria-pressed={soundOn}
            aria-label={soundOn ? "Mute status-change sounds" : "Turn on status-change sounds"}
            title={soundOn ? "Sound on — click to mute" : "Sound off — click to hear a sound when a pump, fire alarm or tank status changes"}
            className={`inline-flex items-center justify-center w-7 h-7 rounded-lg border transition ${soundOn ? "border-orange-300 bg-orange-50 text-orange-600" : "border-gray-200 bg-white text-gray-400 hover:text-gray-600"}`}
            data-testid="live-sound-toggle"
          >
            {soundOn ? <Volume2 className="w-3.5 h-3.5" strokeWidth={2.5} /> : <VolumeX className="w-3.5 h-3.5" strokeWidth={2.5} />}
          </button>
          {gwStatus && (
            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-semibold ${gwStatus.connected ? "bg-emerald-50 border border-emerald-200 text-emerald-700" : "bg-red-50 border border-red-200 text-red-700"}`}>
              {gwStatus.connected ? <Wifi className="w-3.5 h-3.5" strokeWidth={2.5} /> : <WifiOff className="w-3.5 h-3.5" strokeWidth={2.5} />}
              {gwStatus.connected ? "Device Connected" : "Device Offline"}
            </div>
          )}
          {sysvolt !== null && (
            <div className="glass flex items-center gap-1 px-2.5 py-1 rounded-lg" title="Gateway supply voltage (sysvolt)">
              <Zap className="w-3.5 h-3.5 text-yellow-400" strokeWidth={2.5} />
              <span className="font-bold text-gray-800">{sysvolt.toFixed(2)} V</span>
            </div>
          )}
          {systemp !== null && (
            <div className="glass flex items-center gap-1 px-2.5 py-1 rounded-lg" title="Gateway temperature (systemp)">
              <Thermometer className="w-3.5 h-3.5 text-orange-400" strokeWidth={2.5} />
              <span className="font-bold text-gray-800">{systemp.toFixed(1)} °C</span>
            </div>
          )}
          {lastUpdate && (
            <div className="glass px-2.5 py-1 rounded-lg text-gray-500">
              Last data: <span className="text-emerald-500 font-semibold">{fmtTs(lastUpdate)}</span>
            </div>
          )}
        </div>
      </div>

      {!hasAnyAssets && (
        <div className="glass-strong rounded-lg px-4 py-2.5 text-sm text-amber-400 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse inline-block" />
          No assets registered for this site yet. Add pumps, pressure sensors, tanks etc. from the Asset Registries
          in the sidebar to see them here.
        </div>
      )}

      {/* One block per equipment category (Pumps, Pressure, Tanks, …). On desktop the blocks
          split the screen (1 = full, 2 = halves, 3–4 = 2×2, 5–6 = 3×2) and fill the viewport
          height; inside each block the cards are scaled to fit (CategoryBlock). On small screens
          the blocks simply stack at natural size. */}
      {hasAnyAssets && (() => {
        const types = TYPE_ORDER.filter((t) => byType[t]?.length);
        // Always two columns (one if there's a single category). Every block is half width — an
        // odd last block (e.g. Fans & AHU) stays half width rather than stretching across.
        const cols = types.length <= 1 ? 1 : 2;
        const rows = Math.ceil(types.length / cols);
        const renderCard = (type, asset) => {
          const CardComp = CARD_BY_TYPE[type];
          const trendEnabled = TREND_ENABLED_TYPES.has(type);
          const gwOffline = asset.gateway_id && gwStatusMap[asset.gateway_id]?.connected === false;
          const live = gwOffline ? undefined : liveValues[asset.asset_id];
          const ioMapping = asset.gateway_id && asset.io_point_name
            ? ioMap[`${asset.gateway_id}:${asset.io_point_name}`]
            : null;
          // The 24h trend opens in a modal rather than sitting inline, keeping cards short.
          const trend = trendEnabled && asset.gateway_id && asset.io_point_name ? (
            <button
              type="button"
              onClick={() => setTrendAsset({ asset, unit: live?.unit, ioMapping })}
              className="mt-2 self-center inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-gray-200 text-[10px] font-semibold uppercase tracking-wider text-gray-500 hover:text-emerald-600 hover:border-emerald-300 hover:bg-emerald-50 transition-colors"
            >
              <TrendingUp className="w-3 h-3" strokeWidth={2.5} /> 24h trend
            </button>
          ) : null;
          const stale = live?.ingested_at && Date.now() - new Date(live.ingested_at).getTime() > STALE_AFTER_MS;
          return (
            <div className={`relative flex flex-col h-full [&>div]:flex-1 transition-shadow ${flashIds.has(asset.asset_id) ? "rounded-lg ring-2 ring-orange-400" : stale ? "rounded-lg ring-2 ring-slate-300" : ""}`}>
              {stale
                ? <StaleCard type={type} asset={asset} live={live} ioMapping={ioMapping} trend={trend} />
                : <CardComp asset={asset} live={live} ioMapping={ioMapping} trend={trend} />}
            </div>
          );
        };
        // Desktop shows at most two rows of blocks (4 blocks) per screen. With more categories the
        // area scrolls and snaps one row at a time ("block to block"), instead of squeezing every
        // block onto one screen.
        const visibleRows = Math.min(rows, 2);
        const rowHeight = `calc((100vh - 12.5rem - ${(visibleRows - 1) * 6}px) / ${visibleRows})`;
        return (
          <div
            className={isWide ? "h-[calc(100vh-12.5rem)] min-h-[480px] overflow-y-auto snap-y snap-mandatory rounded-md" : ""}
            data-testid="category-viewport"
          >
          <div
            className="grid gap-1.5"
            style={isWide
              ? { gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gridAutoRows: `max(${rows > 2 ? "240px" : "0px"}, ${rowHeight})` }
              : { gridTemplateColumns: "minmax(0, 1fr)" }}
            data-testid="category-blocks"
          >
            {types.map((type) => (
              <CategoryBlock key={type} type={type} assets={byType[type]} renderCard={(a) => renderCard(type, a)} />
            ))}
          </div>
          </div>
        );
      })()}

      <Dialog open={!!trendAsset} onOpenChange={(open) => { if (!open) setTrendAsset(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{trendAsset?.asset.name} — 24h trend</DialogTitle>
          </DialogHeader>
          {trendAsset && (
            <TrendCard selectedSite={selectedSite} asset={trendAsset.asset} unit={trendAsset.unit} ioMapping={trendAsset.ioMapping} />
          )}
        </DialogContent>
      </Dialog>

      {/* Gateway Devices */}
      <section className="glass-strong rounded-lg px-3 py-2 border border-orange-500/10" data-testid="gateway-status-bar">
        {allGateways.length === 0 && !gatewayId ? (
          <div className="text-xs text-gray-400 flex items-center gap-2">
            <Cpu className="w-4 h-4 text-orange-400" strokeWidth={2.5} /> No gateways registered for this site.
          </div>
        ) : (
          <div className="space-y-1">
            {(allGateways.length > 0 ? allGateways : [{ gateway_id: gatewayId, gateway_name: null, hardware_model: null }]).map((gw) => {
              const mappedCount = Object.values(ioMap).filter((m) => m.gateway_id === gw.gateway_id).length;
              return (
                <div key={gw.gateway_id} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                  <div className="flex items-center gap-2 min-w-0">
                    <Cpu className="w-4 h-4 text-orange-400 shrink-0" strokeWidth={2.5} />
                    <span className="text-gray-500">IMEI:</span>
                    <span className="font-mono text-orange-400 font-semibold">{gw.gateway_id}</span>
                    {gw.gateway_name && <span className="text-gray-500 truncate">· {gw.gateway_name}</span>}
                    {gw.hardware_model && <span className="px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 text-[10px] font-medium">{gw.hardware_model}</span>}
                  </div>
                  {gw.gateway_id === gatewayId && gwStatus && (
                    <>
                      <span className={gwStatus.connected ? "text-emerald-500 font-bold" : "text-red-400 font-bold"}>
                        {gwStatus.connected ? "● Connected" : "● Offline"}
                      </span>
                      {gwStatus.peer_ip && (
                        <div><span className="text-gray-500">IP:</span> <span className="text-gray-700 font-mono">{gwStatus.peer_ip}</span></div>
                      )}
                      {gwStatus.last_seen_at && (
                        <div><span className="text-gray-500">Last seen:</span> <span className="text-gray-700">{new Date(gwStatus.last_seen_at).toLocaleTimeString("en-IN")}</span></div>
                      )}
                    </>
                  )}
                  {mappedCount > 0 && (
                    <div className="ml-auto text-gray-400">{mappedCount} IO points mapped</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
